import React, { useState, useEffect } from 'react';
import {
	Text,
	Box,
	Button,
	Flex,
	FormControl,
	FormLabel,
	Input,
	InputGroup,
	InputRightElement,
	IconButton,
	Stack,
	Image,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import { connect } from 'react-redux';
import { login } from '../../redux/actions/loginActions';
import Alert from '../../component/alert';
import { useNavigate } from 'react-router-dom';
import logoImage from '../../assets/logo.png';
import loginImage from '../../assets/login.jpg';

const Login = (props) => {
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [showPassword, setShowPassword] = useState(false);
	const navigate = useNavigate();

	useEffect(() => {
		if (props.isAuthenticated) {
			if (props.isAdmin) {
				return navigate('/admin');
			} else {
				return navigate('/store');
			}
		}
	}, [props.isAuthenticated, navigate, props.isAdmin]);

	const handleSubmit = async (event: React.FormEvent) => {
		event.preventDefault();
		await props.login(email, password);
	};

	return (
		<Flex minH="100vh">
			{/* Left panel — branding */}
			<Flex
				display={{ base: 'none', md: 'flex' }}
				flex={1}
				direction="column"
				align="center"
				justify="center"
				bg="linear-gradient(160deg, #0f3460 0%, #16213e 60%, #1a4731 100%)"
				p={12}
				position="relative"
				overflow="hidden"
			>
				{/* Background image overlay */}
				<Box
					position="absolute"
					inset={0}
					bgImage={`url(${loginImage})`}
					bgSize="cover"
					bgPosition="center"
					opacity={0.08}
				/>

				{/* Content */}
				<Box position="relative" textAlign="center" maxW="360px">
					<Box bg="white" borderRadius="xl" px={6} py={4} display="inline-block" mb={10} boxShadow="xl">
						<Image src={logoImage} alt="Logo" h="48px" />
					</Box>
					<Text fontSize="3xl" fontWeight="bold" color="white" mb={3} lineHeight="shorter">
						Your Supply,<br />Simplified.
					</Text>
					<Text fontSize="md" color="whiteAlpha.700" lineHeight="tall">
						Browse thousands of products and place orders tailored to your facility — all in one place.
					</Text>

					{/* Decorative dots */}
					<Flex justify="center" gap={2} mt={10}>
						<Box w={2} h={2} borderRadius="full" bg="green.400" />
						<Box w={2} h={2} borderRadius="full" bg="whiteAlpha.400" />
						<Box w={2} h={2} borderRadius="full" bg="whiteAlpha.400" />
					</Flex>
				</Box>
			</Flex>

			{/* Right panel — form */}
			<Flex
				flex={{ base: 1, md: '0 0 440px' }}
				direction="column"
				align="center"
				justify="center"
				bg="white"
				px={{ base: 6, md: 12 }}
				py={12}
			>
				{/* Mobile logo */}
				<Box display={{ base: 'block', md: 'none' }} mb={8}>
					<Image src={logoImage} alt="Logo" h="40px" />
				</Box>

				<Box w="full" maxW="360px">
					<Alert />

					<Text fontSize="2xl" fontWeight="bold" color="gray.800" mb={1}>
						Welcome back
					</Text>
					<Text fontSize="sm" color="gray.500" mb={8}>
						Sign in to access your account
					</Text>

					<form onSubmit={handleSubmit}>
						<Stack spacing={5}>
							<FormControl isRequired>
								<FormLabel fontSize="sm" color="gray.600">Email address</FormLabel>
								<Input
									type="email"
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									focusBorderColor="green.400"
									placeholder="you@company.com"
									borderRadius="lg"
								/>
							</FormControl>

							<FormControl isRequired>
								<FormLabel fontSize="sm" color="gray.600">Password</FormLabel>
								<InputGroup>
									<Input
										type={showPassword ? 'text' : 'password'}
										value={password}
										onChange={(e) => setPassword(e.target.value)}
										focusBorderColor="green.400"
										placeholder="Enter your password"
										borderRadius="lg"
									/>
									<InputRightElement>
										<IconButton
											aria-label="Toggle password"
											icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
											size="xs"
											variant="ghost"
											color="gray.400"
											onClick={() => setShowPassword((v) => !v)}
										/>
									</InputRightElement>
								</InputGroup>
							</FormControl>

							<Button
								colorScheme="green"
								type="submit"
								w="full"
								size="lg"
								borderRadius="xl"
								fontWeight="bold"
								bg="green.500"
								_hover={{ bg: 'green.600' }}
								mt={2}
							>
								Sign In
							</Button>
						</Stack>
					</form>

					<Stack spacing={3} mt={8} align="center">
						<Text fontSize="sm" color="gray.500">
							Don't have an account?{' '}
							<Text as="span" color="green.600" fontWeight="semibold" cursor="pointer"
								onClick={() => window.location.href = '/contact'}>
								Contact us to get set up
							</Text>
						</Text>
						<Text fontSize="sm" color="gray.400">or</Text>
						<Button
							variant="ghost"
							colorScheme="green"
							size="sm"
							borderRadius="full"
							onClick={() => navigate('/catalog')}
						>
							Browse the catalog without logging in →
						</Button>
					</Stack>
				</Box>
			</Flex>
		</Flex>
	);
};

const mapStateToProps = (state) => ({
	isAuthenticated: state.auth.isAuthenticated,
	isAdmin: state.auth.isAdmin,
});

const mapDispatchToProps = {
	login: login,
};

export default connect(mapStateToProps, mapDispatchToProps)(Login);
